/**
 * System email — the SES sender everything goes out from.
 *
 * This is the single point of failure for the whole product: sign-in links,
 * invitations and acknowledgement reminders all leave through it. Misconfigure
 * it and every user at every hospital is locked out with nobody able to sign
 * in and fix it. So the page leads with whether it actually works, and offers
 * a test that proves it rather than a form that merely saves.
 *
 * The access keys are never read back. The backend returns `hasAccessKeyId`
 * rather than the key itself, because a credential that can be displayed is a
 * credential that can be copied off a screen.
 */

import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { call, isCallFailure } from '../../lib/callable';
import { Spinner } from '../../app/Spinner';
import { PageState } from '../../shared/PageState';
import own from './Console.module.css';
import page from '../../shared/Page.module.css';
import form from '../../shared/Form.module.css';
import { CODE_MAX, EMAIL_MAX, ORG_NAME_MAX, SECRET_MAX } from '../../types/fieldLimits';

interface EmailConfig {
  amazonSes?: {
    region?: string;
    hasAccessKeyId?: boolean;
    hasSecretAccessKey?: boolean;
    fromEmail?: string;
    fromName?: string;
  };
}

export function SystemEmailPage() {
  const queryClient = useQueryClient();

  const config = useQuery({
    queryKey: ['system-email'],
    retry: false,
    queryFn: () =>
      call<Record<string, never>, EmailConfig>('sysAdminGetSystemEmailConfig', {}),
  });

  const save = useMutation({
    mutationFn: (input: Record<string, unknown>) =>
      call<Record<string, unknown>, unknown>('sysAdminSaveSystemEmailConfig', input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['system-email'] });
    },
  });

  const rekey = useMutation({
    mutationFn: () => call<Record<string, never>, { rekeyed: number; skipped: number }>('sysAdminRekeyOrgDomains', {}),
  });

  const [testTo, setTestTo] = useState('');
  const test = useMutation({
    mutationFn: (to: string) =>
      call<{ to: string }, unknown>('sysAdminTestSystemEmail', { to }),
  });

  const [region, setRegion] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [fromName, setFromName] = useState('');
  const [accessKeyId, setAccessKeyId] = useState('');
  const [secretAccessKey, setSecretAccessKey] = useState('');
  const [seeded, setSeeded] = useState(false);

  const ses = config.data?.amazonSes;

  // Seed the editable fields once, from what is stored. Not the keys — those
  // never come back from the server.
  if (ses && !seeded) {
    setRegion(ses.region ?? '');
    setFromEmail(ses.fromEmail ?? '');
    setFromName(ses.fromName ?? '');
    setSeeded(true);
  }

  if (config.isLoading) return <Spinner label="Loading email settings" />;
  // Without this a failed read rendered the page as "not configured", which
  // invites somebody to re-enter keys that are already there.
  if (config.error) {
    return (
      <PageState title="Could not load email settings" tone="alert">
        {isCallFailure(config.error) ? config.error.message : 'The settings did not come back. Reload the page to try again.'}
      </PageState>
    );
  }

  const configured = Boolean(ses?.hasAccessKeyId && ses?.hasSecretAccessKey && ses?.fromEmail);

  function submit(e: FormEvent) {
    e.preventDefault();
    save.mutate({
      amazonSes: {
        region: region.trim(),
        fromEmail: fromEmail.trim(),
        fromName: fromName.trim(),
        // Sent only when retyped. Blank means "leave the stored one alone",
        // which is why the fields start empty rather than showing anything.
        ...(accessKeyId.trim() ? { accessKeyId: accessKeyId.trim() } : {}),
        ...(secretAccessKey.trim() ? { secretAccessKey: secretAccessKey.trim() } : {}),
      },
    });
  }

  return (
    <>
      <h1 className={own.title}>System email</h1>
      <p className={own.subtitle}>
        Every sign-in link, invitation and reminder leaves through this sender.
        If it stops working, nobody at any hospital can sign in — and nobody
        can email them to say so.
      </p>

      <div
        className={`${page.notice} ${configured ? page.noticeInfo : page.noticeDanger}`}
        role="status"
      >
        {configured ? (
          <>
            Configured and sending from <strong>{ses?.fromEmail}</strong> in{' '}
            {ses?.region || 'an unset region'}.
          </>
        ) : (
          <>
            <strong>Not configured.</strong> No mail can be sent at all, so no
            new user can sign in anywhere on the system.
          </>
        )}
      </div>

      <form onSubmit={submit} className={own.panel} style={{ padding: 'var(--sp-5)', maxWidth: 620 }}>
        {save.error && (
          <div className={own.error} role="alert">
            {isCallFailure(save.error) ? save.error.message : 'Could not save.'}
          </div>
        )}
        {save.isSuccess && (
          <div className={`${page.notice} ${page.noticeInfo}`} role="status">
            Saved. Send a test below to confirm it actually works.
          </div>
        )}

        <div className={form.field} style={{ marginBottom: 'var(--sp-4)' }}>
          <label className={form.label} htmlFor="ses-region">AWS region</label>
          <input id="ses-region"
            maxLength={CODE_MAX} className={form.input} value={region}
            onChange={(e) => setRegion(e.target.value)} placeholder="us-east-1"
            disabled={save.isPending} />
        </div>

        <div className={form.field} style={{ marginBottom: 'var(--sp-4)' }}>
          <label className={form.label} htmlFor="ses-from">From address</label>
          <input id="ses-from"
            maxLength={EMAIL_MAX} className={form.input} value={fromEmail}
            onChange={(e) => setFromEmail(e.target.value)} placeholder="noreply@somexai.com"
            aria-describedby="ses-from-help" disabled={save.isPending} />
          <p className={form.hint} id="ses-from-help">
            Must be a verified sender in SES, or every message is rejected.
          </p>
        </div>

        <div className={form.field} style={{ marginBottom: 'var(--sp-4)' }}>
          <label className={form.label} htmlFor="ses-from-name">From name</label>
          <input id="ses-from-name"
            maxLength={ORG_NAME_MAX} className={form.input} value={fromName}
            onChange={(e) => setFromName(e.target.value)}
            disabled={save.isPending} />
        </div>

        <div className={form.field} style={{ marginBottom: 'var(--sp-4)' }}>
          <label className={form.label} htmlFor="ses-key">
            Access key id
            {ses?.hasAccessKeyId && <span className={form.optional}> — one is stored</span>}
          </label>
          <input id="ses-key"
            maxLength={SECRET_MAX} className={form.input} value={accessKeyId} autoComplete="off"
            onChange={(e) => setAccessKeyId(e.target.value)}
            aria-describedby="ses-key-help" disabled={save.isPending} />
          <p className={form.hint} id="ses-key-help">
            Leave blank to keep the stored one. Keys are never shown back.
          </p>
        </div>

        <div className={form.field} style={{ marginBottom: 'var(--sp-5)' }}>
          <label className={form.label} htmlFor="ses-secret">
            Secret access key
            {ses?.hasSecretAccessKey && <span className={form.optional}> — one is stored</span>}
          </label>
          <input id="ses-secret"
            maxLength={SECRET_MAX} className={form.input} type="password" autoComplete="off"
            value={secretAccessKey} onChange={(e) => setSecretAccessKey(e.target.value)}
            disabled={save.isPending} />
        </div>

        <button type="submit" className={own.googleBtn}
          style={{ width: 'auto', padding: '9px 16px' }} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </button>
      </form>

      <section className={own.panel} style={{ padding: 'var(--sp-5)', marginTop: 'var(--sp-5)', maxWidth: 620 }}>
        <h2 className={page.sectionTitle}>Send a test</h2>
        <p style={{ color: 'var(--ink-2)', margin: '0 0 var(--sp-4)', lineHeight: 1.55 }}>
          Saving proves the settings were written. Only a delivered message
          proves they work.
        </p>

        {test.error && (
          <div className={own.error} role="alert">
            {isCallFailure(test.error) ? test.error.message : 'The test failed to send.'}
          </div>
        )}
        {test.isSuccess && (
          <div className={`${page.notice} ${page.noticeInfo}`} role="status">
            Sent. If it does not arrive, the sender is not verified in SES.
          </div>
        )}

        <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-end' }}>
          <div className={form.field} style={{ flex: 1 }}>
            <label className={form.label} htmlFor="ses-test">Send to</label>
            <input id="ses-test"
            maxLength={EMAIL_MAX} className={form.input} value={testTo}
              onChange={(e) => setTestTo(e.target.value)} placeholder="you@somexai.com"
              disabled={test.isPending} />
          </div>
          <button type="button" className={own.plainBtn}
            disabled={test.isPending || !testTo.includes('@')}
            onClick={() => test.mutate(testTo.trim())}>
            {test.isPending ? 'Sending…' : 'Send test'}
          </button>
        </div>
      </section>

      <section className={own.panel} style={{ padding: 'var(--sp-5)', marginTop: 'var(--sp-5)', maxWidth: 620 }}>
        <h2 className={page.sectionTitle}>Repair domain records</h2>
        <p className={page.itemMeta} style={{ marginBottom: 'var(--sp-3)' }}>
          Organizations created before September 2026 stored their email domain under the wrong record id,
          so sign-in routing and the single sign-on panel could not see it. Running this moves them. Safe to run again.
        </p>
        {rekey.error && (
          <div className={own.error} role="alert">{isCallFailure(rekey.error) ? rekey.error.message : 'The repair failed.'}</div>
        )}
        {rekey.isSuccess && (
          <div className={`${page.notice} ${page.noticeInfo}`} role="status">
            Repaired {rekey.data.rekeyed} {rekey.data.rekeyed === 1 ? 'record' : 'records'}; {rekey.data.skipped} already correct.
          </div>
        )}
        <button type="button" className={own.plainBtn} disabled={rekey.isPending} onClick={() => rekey.mutate()}>
          {rekey.isPending ? 'Repairing…' : 'Repair domain records'}
        </button>
      </section>
    </>
  );
}
